import { useState } from 'react';
import Table, { Column } from '../../../common/table';
import { useTranslation } from 'react-i18next';
import { formatToShortCst } from '../../../../utils/formatToShortCst';
import React from 'react';
import { Alert } from '../../../../types/Alert';
import { formatToTwoDecimals } from '../../../../utils/formatToTwoDecimals';
import { DeviceData } from '../../../../types/DeviceData';
import { Condition } from '../../../../types/Condition';

interface SummitTableProps {
  alerts: Alert[];
  deviceData: DeviceData[];
  highlightedRowId?: string;
  removeHighlightedRowId?: (id: string | null) => void;
}

export default function SummitTable({
  alerts,
  deviceData,
  highlightedRowId,
  removeHighlightedRowId,
}: SummitTableProps) {
  const { t } = useTranslation();
  const [hoveredRow, setHoveredRow] = useState<{
    visible: boolean;
    content: any;
    top: number;
    left: number;
  }>({ visible: false, content: <>T</>, top: 0, left: 0 });

  const hasActiveAlert = (rowId: string) => {
    return alerts.some(
      (alert) => alert.deviceDataId === rowId && !alert.cleared,
    );
  };

  const handleMouseEnter = (row: any, rowElement: HTMLElement) => {
    const activeAlert = alerts.find(
      (alert) => alert.deviceDataId === row._id && !alert.cleared,
    );

    if (!hasActiveAlert(row._id)) return;

    const rect = rowElement.getBoundingClientRect();

    setHoveredRow({
      visible: true,
      content: (
        <div style={{ width: 'auto', height: 'auto' }}>
          <h1>
            {t('sites.type')}: {activeAlert?.triggerType}
          </h1>
          <h1>
            {t('sites.monitor')}: {activeAlert?.monitor?.name}
          </h1>
          <h1>
            {t('sites.condition')}:{' '}
            {findConditionName(
              // @ts-ignore
              activeAlert?.monitor?.conditions,
              activeAlert?.conditionId,
            )}
          </h1>
          <h1>
            {t('sites.priority')}: P{activeAlert?.priority}
          </h1>
        </div>
      ),
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleMouseLeave = () => {
    setHoveredRow({ ...hoveredRow, visible: false });
  };

  const findConditionName = (conditions: Condition[], conditionId: string) => {
    const condition = conditions?.find(
      (condition) => condition._id === conditionId,
    );

    return condition?.name;
  }

  const columns: Column<any>[] = [
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('sites.createdAt')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        const rowRef = React.createRef<HTMLTableCellElement>();

        return (
          <td
            key={colI}
            ref={rowRef}
            className={`hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500 ${
              hasActiveAlert(row._id) ? 'bg-red-100 text-blue-800' : ''
            }`}
            onMouseEnter={() => handleMouseEnter(row, rowRef.current!)}
            onMouseLeave={handleMouseLeave}
          >
            {formatToShortCst(row.createdAt)}
          </td>
        );
      },
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          l1
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        const rowRef = React.createRef<HTMLTableCellElement>();

        return (
          <td
            key={colI}
            ref={rowRef}
            className={`hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500 ${
              hasActiveAlert(row._id) ? 'bg-red-100 text-blue-800' : ''
            }`}
            onMouseEnter={() => handleMouseEnter(row, rowRef.current!)}
            onMouseLeave={handleMouseLeave}
          >
            {formatToTwoDecimals(row.data?.l1Arms)}
          </td>
        );
      },
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          l2
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        const rowRef = React.createRef<HTMLTableCellElement>();

        return (
          <td
            key={colI}
            ref={rowRef}
            className={`hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500 ${
              hasActiveAlert(row._id) ? 'bg-red-100 text-blue-800' : ''
            }`}
            onMouseEnter={() => handleMouseEnter(row, rowRef.current!)}
            onMouseLeave={handleMouseLeave}
          >
            {formatToTwoDecimals(row.data?.l2Arms)}
          </td>
        );
      },
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          t1
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        const rowRef = React.createRef<HTMLTableCellElement>();

        return (
          <td
            key={colI}
            ref={rowRef}
            className={`hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500 ${
              hasActiveAlert(row._id) ? 'bg-red-100 text-blue-800' : ''
            }`}
            onMouseEnter={() => handleMouseEnter(row, rowRef.current!)}
            onMouseLeave={handleMouseLeave}
          >
            {formatToTwoDecimals(row.data?.t1F)}
          </td>
        );
      },
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          t2
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        const rowRef = React.createRef<HTMLTableCellElement>();

        return (
          <td
            key={colI}
            ref={rowRef}
            className={`hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500 ${
              hasActiveAlert(row._id) ? 'bg-red-100 text-blue-800' : ''
            }`}
            onMouseEnter={() => handleMouseEnter(row, rowRef.current!)}
            onMouseLeave={handleMouseLeave}
          >
            {formatToTwoDecimals(row.data?.t2F)}
          </td>
        );
      },
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          t3
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        const rowRef = React.createRef<HTMLTableCellElement>();

        return (
          <td
            key={colI}
            ref={rowRef}
            className={`hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500 ${
              hasActiveAlert(row._id) ? 'bg-red-100 text-blue-800' : ''
            }`}
            onMouseEnter={() => handleMouseEnter(row, rowRef.current!)}
            onMouseLeave={handleMouseLeave}
          >
            {formatToTwoDecimals(row.data?.t3F)}
          </td>
        );
      },
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          p1
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        const rowRef = React.createRef<HTMLTableCellElement>();

        return (
          <td
            key={colI}
            ref={rowRef}
            className={`hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500 ${
              hasActiveAlert(row._id) ? 'bg-red-100 text-blue-800' : ''
            }`}
            onMouseEnter={() => handleMouseEnter(row, rowRef.current!)}
            onMouseLeave={handleMouseLeave}
          >
            {formatToTwoDecimals(row.data?.p1Psi)}
          </td>
        );
      },
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          p2
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        const rowRef = React.createRef<HTMLTableCellElement>();

        return (
          <td
            key={colI}
            ref={rowRef}
            className={`hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500 ${
              hasActiveAlert(row._id) ? 'bg-red-100 text-blue-800' : ''
            }`}
            onMouseEnter={() => handleMouseEnter(row, rowRef.current!)}
            onMouseLeave={handleMouseLeave}
          >
            {formatToTwoDecimals(row.data?.p2Psi)}
          </td>
        );
      },
    },
  ];

  return (
    <>
      <Table
        removePadding
        columns={columns}
        rows={deviceData.sort(
          (a, b) =>
            // @ts-ignore
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )}
        title={t('sites.deviceData')}
        currentPage={1}
        pageSize={1}
        totalRows={1}
        highlightedRowId={highlightedRowId}
        setHighlightedRowId={removeHighlightedRowId}
      />
    </>
  );
}
